import React, { useState, useEffect } from 'react';
import { Search, X, Plus, Download, Trash2, User, Calendar, Link, Eye, Users, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

const Guests = () => {
  const { user } = useAuth();
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newGuest, setNewGuest] = useState({
    slug: ''
  });
  const [guests, setGuests] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchGuests = async () => {
      try {
        setLoading(true);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('guests')
          .eq('id', user.id)
          .single();

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          return;
        }

        const arr = Array.isArray(profiles?.guests) ? profiles.guests : [];
        setGuests(arr.map((guestString, index) => {
          try {
            const g = JSON.parse(guestString);
            const name = `${g.firstName || 'Guest'} ${g.lastName || ''}`.trim();
            return {
              id: g.id || index + 1,
              name,
              slug: g.slug || '',
              createdAt: g.createdAt || new Date().toISOString(),
              clicks: typeof g.clicks === 'number' ? g.clicks : 0,
              role: g.role || (typeof g.clicks === 'number' ? (g.clicks >= 100 ? 'megafan' : g.clicks >= 50 ? 'superfan' : 'fan') : 'fan'),
            };
          } catch {
            return { 
              id: index + 1, 
              name: 'Guest', 
              slug: '', 
              createdAt: new Date().toISOString(),
              clicks: 0,
              role: 'fan'
            };
          }
        }));
      } catch (error) {
        toast.error('Failed to load guests');
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchGuests();
    }
  }, [user?.id]);

  const filteredGuests = guests.filter(guest => {
    const matchesSearch = guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         guest.slug.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleDeleteGuest = async () => {
    if (!guestToDelete) return;

    try {
      const updatedGuests = guests.filter((g) => g.id !== guestToDelete.id);
      setGuests(updatedGuests);

      const toStore = updatedGuests.map(g => JSON.stringify({
        id: g.id,
        firstName: 'Guest',
        lastName: g.id,
        slug: g.slug || null,
        createdAt: g.createdAt,
        clicks: g.clicks || 0,
        role: g.role || 'fan'
      }))

      const { error } = await supabase
        .from('profiles')
        .update({ guests: toStore })
        .eq('id', user.id);

      if (error) {
        console.error('Error deleting guest:', error);
        toast.error('Failed to delete guest. Please try again.');
        return;
      }

      toast.success('Guest deleted successfully!');
      setIsDeleteDialogOpen(false);
      setGuestToDelete(null);
      if (selectedGuest?.id === guestToDelete.id) {
        setSelectedGuest(null);
      }
    } catch (error) {
      toast.error('Failed to delete guest');
    }
  };

  const exportGuestsToCSV = () => {
    const csvContent = [
      ['Name', 'Slug', 'Created At', 'Clicks', 'Role'].join(','),
      ...guests.map(guest => [
        guest.name,
        guest.slug || 'N/A',
        guest.createdAt,
        guest.clicks || 0,
        guest.role || 'fan'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'guests.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Guest list exported successfully!');
  };

  // Calculate stats
  const totalGuests = guests.length;
  const todayGuests = guests.filter(g => {
    const today = new Date().toDateString();
    const guestDate = new Date(g.createdAt).toDateString();
    return today === guestDate;
  }).length;
  const totalClicks = guests.reduce((sum, guest) => sum + (guest.clicks || 0), 0);
  const avgClicksPerGuest = totalGuests > 0 ? Math.round(totalClicks / totalGuests) : 0;

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Guest Management</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track and manage guest visits and engagement
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportGuestsToCSV}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20
 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalGuests}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Guests
              </p>
            </div>
          </div>

<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {todayGuests}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Today's New Guests
              </p>
            </div>
          </div>

<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {totalClicks}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total Clicks
              </p>
            </div>
          </div>

<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-50 dark:bg-orange-500/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                {avgClicksPerGuest}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Avg Clicks/Guest
              </p>
            </div>
          </div>
        </div>

        {/* Search */}
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search guests by name or slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Guest List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Guests ({filteredGuests.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && (
                  <div className="p-6 text-center">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">Loading guests...</p>
                  </div>
                )}

                {!loading && filteredGuests.length === 0 && (
                  <div className="p-6 text-center">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No guests found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">Add your first guest to get started</p>
                  </div>
                )}

                {!loading && filteredGuests.map((guest) => (
                  <div
                    key={guest.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedGuest?.id === guest.id ? "bg-blue-50 dark:bg-blue-900/20 border-r-4 border-blue-500" : ""
                    }`}
                    onClick={() => setSelectedGuest(guest)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {guest.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-gray-900 dark:text-white truncate">{guest.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {guest.slug && (
                              <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                <Link className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{guest.slug}</span>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {guest.clicks || 0} clicks • Created {new Date(guest.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setGuestToDelete(guest);
                          setIsDeleteDialogOpen(true);
                        }}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Guest Details */}
          <div>
<div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:border-white/20 rounded-xl p-6 shadow-sm">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Guest Details</h2>
              </div>
              
              {selectedGuest ? (
                <div className="p-6 space-y-4">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">
                      {selectedGuest.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedGuest.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Guest ID: {selectedGuest.id}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Link className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Slug</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedGuest.slug || 'Not assigned'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Created</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(selectedGuest.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Time</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(selectedGuest.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <Eye className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Total Clicks</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{selectedGuest.clicks || 0}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <User className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Role</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{selectedGuest.role || 'fan'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <User className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Select a guest to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>

       

        {/* Delete Confirmation Modal */}
        {isDeleteDialogOpen && (
          <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-black rounded-xl shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Delete Guest</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Are you sure you want to delete <strong>{guestToDelete?.name}</strong>? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setGuestToDelete(null);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteGuest}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Guests;